import { StatusBadge, type Tom } from "@/components/data/status-badge";
import {
  dicaDaFormalizacao,
  formalizacaoDoCompartilhamento,
  textoDoLink,
  type CompartilhamentoResumo,
  type EstadoFormalizacao,
  type FormalizacaoDoRomaneio,
} from "@shared/mercado";

/**
 * Onde está a nota do lado do corretor. É um EIXO SEPARADO do dinheiro — "Paga" e
 * "Não formalizada" são um estado válido e mostram dois chips lado a lado.
 * Fundir os dois num só apagaria uma das duas informações (ver StatusBadge).
 *
 * ⚠️ TERMINA EM "FORMALIZADA". Enviado e Visto mediam o link do WhatsApp, e com
 * o comprador no Rizoma o link não é o canal: formalizar já pôs o romaneio na
 * tela dele (#241). Para quem NÃO tem conta o link continua sendo o único
 * caminho, e por isso ele aparece — como SUBLINHA, não como degrau. A derivação
 * é a de `shared/mercado.ts`; aqui só se pinta.
 */

const LABEL: Record<EstadoFormalizacao, string> = {
  NAO_FORMALIZADA: "Não formalizada",
  FORMALIZADA: "Formalizada",
  REVOGADO: "Revogada",
};

/**
 * ⚠️ VERDE É "RESOLVIDO", nos dois lados do balcão — a mesma cor de PAGA.
 * Formalizada é o estado FINAL do caminho da nota; o que ainda pede ação (o
 * link não enviado para quem não tem conta) é âmbar na sublinha, não no chip.
 */
export const TOM_FORMALIZACAO: Record<EstadoFormalizacao, Tom> = {
  NAO_FORMALIZADA: "neutro",
  FORMALIZADA: "sucesso",
  REVOGADO: "erro",
};

export function ChipFormalizacao({
  formalizacao, compradorNome, emAberto = true, testIdSufixo,
}: {
  formalizacao: FormalizacaoDoRomaneio;
  compradorNome: string;
  /** Nota paga: "link não enviado" vira histórico, sem âmbar (ver `textoDoLink`). */
  emAberto?: boolean;
  testIdSufixo?: string;
}) {
  const sublinha = textoDoLink(formalizacao.link, { emAberto });
  const dica = dicaDaFormalizacao(formalizacao, compradorNome);
  const sufixo = testIdSufixo ? `-${testIdSufixo}` : "";
  return (
    <span className="inline-flex flex-col items-start gap-0.5 align-middle" title={dica ?? undefined}>
      <StatusBadge tom={TOM_FORMALIZACAO[formalizacao.estado]} ponto testId={`chip-formalizacao-${formalizacao.estado}${sufixo}`}>
        {LABEL[formalizacao.estado]}
      </StatusBadge>
      {sublinha && (
        <span
          className={`text-[10px] leading-tight ${sublinha.atencao ? "text-atencao font-semibold" : "text-muted-foreground"}`}
          data-testid={`sublinha-link${sufixo}`}
        >
          {sublinha.texto}
        </span>
      )}
    </span>
  );
}

/** O chip da lista de Vendas, a partir do compartilhamento que VALE (ou nenhum). */
export function FormalizacaoChip({
  compartilhamento, compradorNome, emAberto,
}: {
  compartilhamento?: CompartilhamentoResumo;
  compradorNome: string;
  emAberto?: boolean;
}) {
  return (
    <ChipFormalizacao
      formalizacao={formalizacaoDoCompartilhamento(compartilhamento)}
      compradorNome={compradorNome}
      emAberto={emAberto}
    />
  );
}
